import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type * as Y from 'yjs'
import type { WebsocketProvider } from 'y-websocket'
import {
  boundingRect,
  isLightBackground,
  MIN_FONT_SIZE,
  type BoardElement,
  type BoardTheme,
  type FontDto,
  type Frame,
  type MediaDto,
  type Presence,
  type Rect,
} from '@praesto/shared'
import { bringToFront, removeElement, richFragment, updateElement } from '../collab/elements.js'
import { removeFrame, updateFrame } from '../collab/frames.js'
import type { RemotePresence } from '../collab/usePresence.js'
import type { CameraStore } from './CameraStore.js'
import { BoardLayer, type LayerInteractions } from './BoardLayer.js'
import type { ResizeCorner } from './ElementView.js'
import {
  isSelected,
  selectOne,
  selectedIds,
  toggleSelection,
  type Selection,
} from './selection.js'
import { useText } from '../i18n.js'
import { FrameGrid } from './FrameGrid.js'
import {
  anchorFor,
  axisOf,
  collectTargets,
  edgesX,
  edgesY,
  findSnap,
  snapResize,
  SNAP_PX,
  type SnapTargets,
} from './snapping.js'

interface Props {
  camera: CameraStore
  doc: Y.Doc
  provider: WebsocketProvider | null
  elements: BoardElement[]
  frames: Frame[]
  mediaById: Map<string, MediaDto>
  presence: RemotePresence[]
  canEdit: boolean
  selection: Selection
  onSelect: (selection: Selection) => void
  onDropFiles: (files: File[], boardX: number, boardY: number) => void
  /** Wird gerufen, sobald der Nutzer die Ansicht selbst bewegt (Follow-Modus verlassen). */
  onUserCameraInput?: () => void
  /** Hintergrundfarbe des Boards (liegt im Yjs-Dokument). */
  background: string
  theme: BoardTheme
  /** Schriften der Organisation — die Textleiste bietet sie zur Wahl an. */
  fonts?: readonly FontDto[]
  /**
   * Darf die eingebettete Seite bedient werden?
   *
   * Bewusst getrennt von `viewLocked`: Im Vortrag steht die Ansicht fest, die
   * Website soll aber gerade dort bedienbar sein. Nur streng geführte Gäste
   * bekommen beides gesperrt.
   */
  allowIframeInteraction?: boolean
  /**
   * Schreibrecht am Board-Dokument. Unabhängig von `canEdit`, das nur die
   * Werkzeuge steuert — im Vortrag ist Bearbeiten aus, Schreiben aber erlaubt.
   */
  canWrite?: boolean
  onEditIframeUrl: (id: string) => void
  /** Frame samt Inhalt als Vorlage sichern; fehlt ohne Bearbeitungsrecht. */
  onSaveFrameTemplate?: (frame: Frame) => void
  /**
   * Sperrt jede eigene Navigation. Gilt im Freigabe-Modus „streng geführt":
   * dort bestimmt ausschließlich der Präsentierende, was der Gast sieht.
   */
  viewLocked?: boolean
  /**
   * „Frei ziehen" ist scharf: Der nächste Zug im Leerraum spannt einen Frame
   * auf, statt die Ansicht zu verschieben.
   */
  frameDraw?: boolean
  /** Der aufgezogene Frame steht fest. */
  onFrameDrawn?: (rect: Rect) => void
  /** Abgebrochen — durch Escape oder einen Zug ohne Fläche. */
  onFrameDrawCancel?: () => void
}

type Target = { type: 'element' | 'frame'; id: string }

interface DragChild {
  target: Target
  baseX: number
  baseY: number
}

/**
 * Ein Objekt, das mit seinem Frame mitskaliert — samt allem, was neben der
 * Geometrie mitwachsen muss.
 */
interface ScaleChild {
  target: Target
  x: number
  y: number
  w: number
  h: number
  /** Nur bei Textelementen: Ohne Mitskalieren bliebe die Schrift stehen. */
  fontSize?: number
  /** Nur bei Frames: Die Stärke steht in Board-Einheiten, nicht in Pixeln. */
  borderWidth?: number
}

type Interaction =
  | { kind: 'pan'; lastX: number; lastY: number }
  | {
      kind: 'pinch'
      /** Abstand und Mittelpunkt der beiden Finger beim letzten Bild. */
      lastDistance: number
      lastCenterX: number
      lastCenterY: number
    }
  | {
      /** Auswahlrahmen: Umschalt + Ziehen im Leerraum. */
      kind: 'marquee'
      startX: number
      startY: number
      /**
       * Die Auswahl beim Aufziehen. Der Rahmen nimmt hinzu, statt zu ersetzen —
       * er wird ja mit derselben Umschalttaste aufgezogen, die auch beim Klicken
       * hinzunimmt.
       */
      base: Selection
      /** Der aufgezogene Rahmen — hier und nicht im Zustand, damit das
          Loslassen ihn ohne veralteten Abschluss lesen kann. */
      rect: Rect | null
    }
  | {
      /** Frei aufgezogener Frame — dieselbe Geste wie der Auswahlrahmen,
          aber mit einem Frame am Ende statt einer Auswahl. */
      kind: 'frameDraw'
      startX: number
      startY: number
      rect: Rect | null
    }
  | {
      kind: 'drag'
      target: Target
      grabX: number
      grabY: number
      baseX: number
      baseY: number
      w: number
      h: number
      /** Objekte, die mit dem gezogenen Frame mitwandern. */
      children: DragChild[]
      /** Linien, an denen eingerastet wird, samt umgebendem Frame. */
      targets: SnapTargets
    }
  | {
      kind: 'resize'
      target: Target
      anchorX: number
      anchorY: number
      aspect: number | null
      targets: SnapTargets
      /** Der Frame beim Anfassen — Bezugsgröße für den Maßstab. */
      base: Rect
      /** Kanten-Anfasser rühren nur eine Achse an. */
      axis: { x: boolean; y: boolean }
      /** Inhalt, der mitskaliert wird. Leer beim gewöhnlichen Größenändern. */
      content: ScaleChild[]
      /** Wann der Inhalt zuletzt geschrieben wurde (Drosselung). */
      lastContentWrite: number
      /**
       * Der zuletzt berechnete Rahmen, der noch nicht auf den Inhalt angewandt
       * wurde. Beim Loslassen wird er nachgeholt — sonst bliebe der Inhalt um
       * bis zu eine Drosselstufe hinter dem Frame zurück.
       */
      pendingRect: Rect | null
    }
  | null

/** Kleinste sinnvolle Größe in Board-Koordinaten. */
const MIN_SIZE = 24

/** Zwei Finger, die praktisch aufeinanderliegen, ergeben keinen Maßstab. */
const MIN_PINCH_DISTANCE = 12

/**
 * Abstand zwischen zwei Schreibvorgängen am mitskalierten Inhalt.
 *
 * Der Frame folgt dem Zeiger in jedem Bild — er ist das, was man beim Ziehen
 * ansieht. Der Inhalt darf gröber nachziehen, denn er kostet je Schreibvorgang
 * so viel, wie er Objekte hat.
 *
 * Gemessen an einem Frame mit 150 Elementen, ein Ziehvorgang über rund vier
 * Sekunden, gezählt an `WebSocket.send`:
 *
 * | Abstand | über die Leitung | schlechtester Bildabstand |
 * |---------|------------------|---------------------------|
 * | ohne    | 847 KB           | 83 ms                     |
 * | 33 ms   | 721 KB           | 67 ms                     |
 * | 70 ms   | 359-390 KB       | 50 ms                     |
 *
 * 33 ms brachten kaum etwas, weil der Browser beim Ziehen ohnehin nur rund
 * dreißig Bilder je Sekunde schafft und damit fast jede Bewegung durchließ.
 * Bei 70 ms zieht der Inhalt für das Auge noch mit, und die Last der anderen
 * Teilnehmer sinkt um gut die Hälfte.
 *
 * Zum Vergleich: derselbe Zug ohne mitskalierten Inhalt kostet 27 KB. Teuer
 * bleibt es also auch gedrosselt — nur nicht mehr um das Dreißigfache.
 *
 * Unterhalb der Schwelle wird gar nicht gedrosselt: Bei einer Handvoll Objekte
 * kostet das Schreiben nichts, und dann soll auch nichts nachhinken.
 */
const CONTENT_WRITE_MS = 70
const THROTTLE_ABOVE = 12

/**
 * „Inhalt mitskalieren" ist eine Vorliebe der Person, kein Merkmal des Boards.
 *
 * Deshalb im Browser und nicht im Dokument: Zwei Leute am selben Board dürfen
 * verschieden arbeiten, und ein Modus, der bei den anderen mit umspringt, wäre
 * ein Übergriff.
 */
const SCALE_CONTENT_KEY = 'praesto.scaleContent'

function readScaleContent(): boolean {
  try {
    return localStorage.getItem(SCALE_CONTENT_KEY) === '1'
  } catch {
    return false
  }
}

export function Canvas({
  camera,
  doc,
  provider,
  elements,
  frames,
  mediaById,
  presence,
  canEdit,
  selection,
  onSelect,
  onDropFiles,
  onUserCameraInput,
  background,
  theme,
  fonts = [],
  allowIframeInteraction = true,
  canWrite = false,
  onEditIframeUrl,
  onSaveFrameTemplate,
  viewLocked = false,
  frameDraw = false,
  onFrameDrawn,
  onFrameDrawCancel,
}: Props): React.ReactElement {
  const t = useText()
  const containerRef = useRef<HTMLDivElement>(null)
  const layerRef = useRef<HTMLDivElement>(null)
  const interaction = useRef<Interaction>(null)
  const spaceHeld = useRef(false)
  /**
   * Alle Finger, die gerade auf dem Schirm liegen.
   *
   * Auf dem Tablet gibt es kein Mausrad und keine Leertaste — der zweite Finger
   * *ist* die Zoomsteuerung. Ohne diese Buchführung wäre ein Board auf dem
   * Telefon nur zu verschieben und nie zu vergrößern.
   */
  const touches = useRef(new Map<number, { x: number; y: number }>())
  const [cursorStyle, setCursorStyle] = useState<'default' | 'grab' | 'grabbing'>('default')
  const [dropActive, setDropActive] = useState(false)
  const [guides, setGuides] = useState<{ v: number[]; h: number[] }>({ v: [], h: [] })
  // Der Frame, in dem gerade gearbeitet wird. Sein Raster wird währenddessen
  // blass eingeblendet — sonst wäre es unsichtbar und niemand fände es.
  const [activeFrame, setActiveFrame] = useState<Frame | null>(null)
  /** Der aufgezogene Auswahlrahmen in Board-Koordinaten, solange er läuft. */
  const [marquee, setMarquee] = useState<Rect | null>(null)
  /** Nimmt ein Frame beim Größenändern seinen Inhalt mit? */
  const [scaleContent, setScaleContent] = useState(readScaleContent)

  // Callback-Ref, damit der Aufrufer den Follow-Modus verlassen kann, ohne dass
  // sich die Eingabe-Effekte bei jeder Zustandsänderung neu registrieren.
  const breakAway = useRef(onUserCameraInput)
  breakAway.current = onUserCameraInput

  const applyGeometry = useCallback(
    (target: Target, patch: Partial<Rect>): void => {
      if (target.type === 'element') updateElement(doc, target.id, patch)
      else updateFrame(doc, target.id, patch)
    },
    [doc],
  )

  // --- Größe & Layer-Anbindung ---------------------------------------------

  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container) return
    camera.attachLayer(layerRef.current)

    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return
      camera.setSize({ width: entry.contentRect.width, height: entry.contentRect.height })
    })
    observer.observe(container)
    camera.setSize({ width: container.clientWidth, height: container.clientHeight })

    return () => {
      observer.disconnect()
      camera.attachLayer(null)
    }
  }, [camera])

  // --- Presence: eigenen Viewport melden ------------------------------------

  /*
   * Eigenen Ausschnitt melden — mit zwei bewussten Drosselungen.
   *
   * Wer einen Frame zeigt, braucht gar nicht zu senden: die Gäste folgen dem
   * Frame und animieren selbst. Täte er es doch, prasselten während seiner
   * 600-ms-Fahrt rund 36 Aktualisierungen auf jeden Gast ein, jede mit einem
   * React-Render — und genau das bringt die Animation des Gastes ins Stocken.
   *
   * Alle anderen senden gedrosselt: ihr Ausschnitt speist nur die Gast-Vorschau
   * des Präsentierenden, dafür reichen zehn Bilder pro Sekunde. Voll ausgereizt
   * wird die Bildwiederholrate nur beim freien Navigieren des Präsentierenden —
   * da ist der Ausschnitt selbst das Signal, dem die Gäste folgen.
   */
  useEffect(() => {
    if (!provider) return
    const THROTTLE_MS = 100
    let scheduled = false
    let lastPublish = 0

    const publish = (): void => {
      if (scheduled) return
      scheduled = true
      requestAnimationFrame(() => {
        scheduled = false
        const state = provider.awareness.getLocalState() as Presence | null
        if (!state) return
        if (state.presenting && state.focus) return

        const now = performance.now()
        if (!state.presenting && now - lastPublish < THROTTLE_MS) return
        lastPublish = now
        provider.awareness.setLocalStateField('viewport', camera.getViewport())
      })
    }
    publish()
    return camera.subscribe(publish)
  }, [camera, provider])

  // Zeigerbewegungen kommen mit bis zu 120 Ereignissen pro Sekunde herein. Jedes
  // davon als Awareness-Nachricht zu verschicken wäre reine Netzlast — ein
  // Frame Auflösung reicht für einen flüssig wirkenden Fremdcursor vollkommen.
  const pendingCursor = useRef<{ x: number; y: number } | null>(null)
  const cursorScheduled = useRef(false)

  const publishCursor = useCallback(
    (boardX: number | null, boardY: number | null) => {
      if (!provider) return
      pendingCursor.current = boardX === null || boardY === null ? null : { x: boardX, y: boardY }
      if (cursorScheduled.current) return
      cursorScheduled.current = true
      requestAnimationFrame(() => {
        cursorScheduled.current = false
        const state = provider.awareness.getLocalState() as Presence | null
        // Ausgeblendeter Zeiger heißt: Es geht nichts hinaus. Die Position im
        // Netz zu verteilen und beim Empfänger nicht zu zeichnen wäre eine
        // Zusage, die von der Oberfläche abhängt statt von der Leitung.
        const hidden = state?.presenting === true && !state.pointer
        provider.awareness.setLocalStateField('cursor', hidden ? null : pendingCursor.current)
      })
    },
    [provider],
  )

  // --- Zoom & Pan über das Mausrad ------------------------------------------

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const onWheel = (event: WheelEvent): void => {
      // preventDefault auch im gesperrten Zustand: sonst scrollt stattdessen die
      // Seite und der Gast landet neben dem Board.
      event.preventDefault()
      if (viewLocked) return
      breakAway.current?.()
      const rect = container.getBoundingClientRect()
      const anchor = { x: event.clientX - rect.left, y: event.clientY - rect.top }

      // Trackpad-Pinch meldet sich als ctrl+wheel — das ist zuverlässig.
      // Ein klassisches Mausrad erkennt man an zeilenweisem deltaMode oder an
      // groben, ganzzahligen Sprüngen ohne horizontalen Anteil. Alles andere
      // behandeln wir als Zwei-Finger-Scrollen, also als Verschieben.
      const isPinch = event.ctrlKey || event.metaKey
      const isMouseWheel =
        event.deltaMode !== 0 ||
        (event.deltaX === 0 && Math.abs(event.deltaY) >= 50 && Number.isInteger(event.deltaY))

      if (isPinch || isMouseWheel) {
        // Exponentiell, damit sich jeder Rastschritt gleich stark anfühlt —
        // egal, ob man bei 5 % oder bei 400 % Zoom steht.
        const intensity = isPinch ? 0.01 : 0.0025
        camera.zoomAt(anchor, Math.exp(-event.deltaY * intensity))
      } else {
        camera.panByScreen(-event.deltaX, -event.deltaY)
      }
    }

    // `passive: false`, sonst darf preventDefault den Browser-Zoom nicht stoppen.
    container.addEventListener('wheel', onWheel, { passive: false })
    return () => container.removeEventListener('wheel', onWheel)
  }, [camera, viewLocked])

  // --- Tastatur ---------------------------------------------------------------

  useEffect(() => {
    const isTypingTarget = (target: EventTarget | null): boolean =>
      target instanceof HTMLElement &&
      (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)

    const onKeyDown = (event: KeyboardEvent): void => {
      if (isTypingTarget(event.target) || viewLocked) return

      if (event.code === 'Space' && !spaceHeld.current) {
        spaceHeld.current = true
        setCursorStyle('grab')
        event.preventDefault()
        return
      }
      if ((event.key === 'Delete' || event.key === 'Backspace') && selection && canEdit) {
        event.preventDefault()
        // In einer Transaktion: Zehn gelöschte Elemente sollen bei den anderen
        // Teilnehmern als ein Schritt ankommen und als einer zurückzunehmen sein.
        doc.transact(() => {
          for (const id of selection.ids) {
            if (selection.kind === 'element') removeElement(doc, id)
            else removeFrame(doc, id)
          }
        })
        onSelect(null)
        return
      }
      // Alles auswählen meint die Elemente: Frames sind Struktur, und wer sie
      // alle zugleich verschiebt, verschiebt das ganze Board.
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'a' && canEdit) {
        event.preventDefault()
        if (elements.length > 0) onSelect({ kind: 'element', ids: elements.map((e) => e.id) })
        return
      }
      if (event.key === 'Escape') onSelect(null)
      if (event.key === '0') {
        const bounds = boundingRect([...elements, ...frames])
        if (bounds) {
          breakAway.current?.()
          camera.animateToRect(bounds)
        }
      }
      if (event.key === '1') {
        breakAway.current?.()
        camera.animateTo({ ...camera.getCamera(), zoom: 1 }, 300)
      }
      if (event.key === '+' || event.key === '=' || event.key === '-') {
        breakAway.current?.()
        const size = camera.getSize()
        camera.zoomAt({ x: size.width / 2, y: size.height / 2 }, event.key === '-' ? 0.8 : 1.25)
      }
    }

    const onKeyUp = (event: KeyboardEvent): void => {
      if (event.code === 'Space') {
        spaceHeld.current = false
        setCursorStyle('default')
      }
    }

    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
    }
  }, [camera, canEdit, doc, elements, frames, onSelect, selection, viewLocked])

  // --- Zeigereingaben ---------------------------------------------------------

  const boardPointFromEvent = useCallback(
    (event: { clientX: number; clientY: number }) => {
      const rect = containerRef.current?.getBoundingClientRect()
      if (!rect) return { x: 0, y: 0 }
      return camera.screenToBoard({ x: event.clientX - rect.left, y: event.clientY - rect.top })
    },
    [camera],
  )

  const startPan = useCallback((event: { clientX: number; clientY: number }) => {
    if (viewLocked) return
    /*
     * Eine laufende Zwei-Finger-Geste hat Vorrang.
     *
     * Sie wird in der Erfassungsphase erkannt, der reguläre Handler läuft
     * danach — ohne diese Sperre machte er aus dem gerade begonnenen Zoomen
     * wieder ein Verschieben, und die Geste wirkte gar nicht.
     */
    if (interaction.current?.kind === 'pinch') return
    breakAway.current?.()
    interaction.current = { kind: 'pan', lastX: event.clientX, lastY: event.clientY }
    setCursorStyle('grabbing')
  }, [viewLocked])

  const onBackgroundPointerDown = useCallback(
    (event: React.PointerEvent) => {
      if (event.button !== 0 && event.button !== 1) return
      // Zweiter Finger: Die Geste läuft schon, hier ist nichts mehr zu tun —
      // und vor allem darf die Auswahl nicht aufgehoben werden.
      if (interaction.current?.kind === 'pinch') return

      /*
       * Umschalt + Ziehen im Leerraum zieht einen Auswahlrahmen auf.
       *
       * Das gewöhnliche Ziehen bleibt das Verschieben der Ansicht: Præsto ist
       * eine unendliche Fläche, auf der man sich vor allem bewegt. Den Rahmen an
       * eine Zusatztaste zu hängen kostet einen Finger und erspart, dass jedes
       * Verschieben zuerst eine Auswahl aufhebt.
       */
      /*
       * „Frei ziehen" geht allem anderen vor: Der Modus ist bewusst gewählt
       * worden, und solange er läuft, soll ein Zug nichts anderes tun — auch
       * nicht über einem vorhandenen Objekt. Dass der Zug dort überhaupt
       * ankommt, besorgt `pointer-events: none` auf dem Layer (siehe CSS).
       */
      if (frameDraw && canEdit) {
        const point = boardPointFromEvent(event)
        interaction.current = {
          kind: 'frameDraw',
          startX: point.x,
          startY: point.y,
          rect: null,
        }
        setMarquee({ x: point.x, y: point.y, w: 0, h: 0 })
        return
      }

      if (event.shiftKey && canEdit) {
        const point = boardPointFromEvent(event)
        interaction.current = {
          kind: 'marquee',
          startX: point.x,
          startY: point.y,
          base: selection,
          rect: null,
        }
        setMarquee({ x: point.x, y: point.y, w: 0, h: 0 })
        return
      }

      // Leerraum: Auswahl aufheben und pannen.
      onSelect(null)
      if (viewLocked) return
      startPan(event)
    },
    [boardPointFromEvent, canEdit, frameDraw, onSelect, selection, startPan, viewLocked],
  )

  /**
   * Was mit einem Frame mitwandert.
   *
   * Zuordnung über Geometrie statt über ein Feld im Datenmodell: Was vollständig
   * im Frame liegt, gehört zu ihm. Das entspricht dem, was man beim Anlegen
   * eines Frames ohnehin tut — man rahmt Inhalte ein — und braucht keine
   * zusätzliche Bedienung. Verschachtelte Frames kommen mit, ihre Inhalte
   * liegen dann ebenfalls im äußeren Frame und werden mit erfasst.
   */
  const childrenOfFrame = useCallback(
    (frame: Rect, frameId: string): DragChild[] => {
      const contains = (box: Rect): boolean =>
        box.x >= frame.x - 0.5 &&
        box.y >= frame.y - 0.5 &&
        box.x + box.w <= frame.x + frame.w + 0.5 &&
        box.y + box.h <= frame.y + frame.h + 0.5

      const children: DragChild[] = []
      for (const element of elements) {
        if (contains(element)) {
          children.push({ target: { type: 'element', id: element.id }, baseX: element.x, baseY: element.y })
        }
      }
      for (const other of frames) {
        if (other.id !== frameId && contains(other)) {
          children.push({ target: { type: 'frame', id: other.id }, baseX: other.x, baseY: other.y })
        }
      }
      return children
    },
    [elements, frames],
  )

  /**
   * Was mit einem Frame mitskaliert — dieselbe Regel wie beim Verschieben:
   * was vollständig darin liegt.
   *
   * Gesammelt wird einmal beim Anfassen. Währenddessen dieselbe Liste neu zu
   * bilden ginge schief, sobald der Frame kleiner wird als sein Inhalt: Die
   * Kinder fielen der Reihe nach aus der Prüfung und blieben stehen.
   */
  const contentOfFrame = useCallback(
    (frame: Rect, frameId: string): ScaleChild[] => {
      const contains = (box: Rect): boolean =>
        box.x >= frame.x - 0.5 &&
        box.y >= frame.y - 0.5 &&
        box.x + box.w <= frame.x + frame.w + 0.5 &&
        box.y + box.h <= frame.y + frame.h + 0.5

      const content: ScaleChild[] = []
      for (const element of elements) {
        if (!contains(element)) continue
        content.push({
          target: { type: 'element', id: element.id },
          x: element.x,
          y: element.y,
          w: element.w,
          h: element.h,
          ...(element.type === 'text' ? { fontSize: element.fontSize } : {}),
        })
      }
      for (const other of frames) {
        if (other.id === frameId || !contains(other)) continue
        content.push({
          target: { type: 'frame', id: other.id },
          x: other.x,
          y: other.y,
          w: other.w,
          h: other.h,
          borderWidth: other.borderWidth,
        })
      }
      return content
    },
    [elements, frames],
  )

  /*
   * Bewusst ohne `setPointerCapture`: Bewegung und Loslassen werden auf `window`
   * abgehört, die Umleitung bringt also nichts — kostet aber Klick- und
   * Doppelklick-Ereignisse, weil die anschließend beim Capture-Ziel landen statt
   * beim tatsächlich getroffenen Element. Genau daran scheiterte der Doppelklick
   * auf eine eingebettete Website.
   */
  /**
   * Die übrigen Objekte der Auswahl — sie wandern mit dem angefassten mit.
   *
   * Ohne das wäre eine Mehrfachauswahl bloß eine Anzeige: Man sähe fünf Rahmen
   * und verschöbe doch nur einen.
   *
   * Frames nehmen dabei ihren Inhalt mit, auch wenn nicht sie selbst angefasst
   * wurden: Ein Frame, der bei der Mehrfachauswahl leer zurückbliebe, verhielte
   * sich anders als derselbe Frame einzeln gezogen.
   */
  const selectedCompanions = useCallback(
    (target: Target): DragChild[] => {
      const ids = selectedIds(selection, target.type)
      if (ids.length < 2 || !ids.includes(target.id)) return []

      const source = target.type === 'element' ? elements : frames
      const companions: DragChild[] = []
      const seen = new Set([target.id])
      const add = (child: DragChild): void => {
        if (seen.has(child.target.id)) return
        seen.add(child.target.id)
        companions.push(child)
      }

      for (const id of ids) {
        if (id === target.id) continue
        const box = source.find((candidate) => candidate.id === id)
        if (!box) continue
        add({ target: { type: target.type, id }, baseX: box.x, baseY: box.y })
        if (target.type === 'frame') for (const child of childrenOfFrame(box, id)) add(child)
      }
      return companions
    },
    [childrenOfFrame, elements, frames, selection],
  )

  const beginDrag = useCallback(
    (event: React.PointerEvent, target: Target, box: Rect) => {
      if (event.button !== 0) return
      event.stopPropagation()

      // Der zweite Finger auf einem Element zoomt, statt es zu verschieben.
      if (interaction.current?.kind === 'pinch') return

      // Leertaste hat Vorrang: dann pannt man auch über Objekten hinweg,
      // statt sie versehentlich zu verschieben.
      if (spaceHeld.current) {
        startPan(event)
        return
      }

      const point = boardPointFromEvent(event)
      const children = [
        ...(target.type === 'frame' ? childrenOfFrame(box, target.id) : []),
        ...selectedCompanions(target),
      ]
      // Einmal beim Anfassen sammeln statt bei jeder Mausbewegung: die Kanten
      // der übrigen Objekte ändern sich während des Ziehens ohnehin nicht.
      const moved = new Set([target.id, ...children.map((child) => child.target.id)])
      const targets = collectTargets({ box, elements, frames, moving: moved })
      setActiveFrame(targets.frame)
      interaction.current = {
        kind: 'drag',
        target,
        grabX: point.x - box.x,
        grabY: point.y - box.y,
        baseX: box.x,
        baseY: box.y,
        w: box.w,
        h: box.h,
        children,
        targets,
      }
    },
    [boardPointFromEvent, childrenOfFrame, elements, frames, selectedCompanions, startPan],
  )

  const beginResize = useCallback(
    (
      event: React.PointerEvent,
      target: Target,
      box: Rect,
      corner: ResizeCorner,
      aspect: number | null,
      /**
       * Schriftgröße eines Textfelds, das sie mitnehmen soll — sonst nicht
       * gesetzt. Das Feld ist dann sein eigener „Inhalt": Es skaliert wie der
       * Inhalt eines Frames, nur dass hier nichts als die Schrift mitwächst.
       */
      fontSize?: number,
    ) => {
      if (event.button !== 0) return
      event.stopPropagation()
      if (interaction.current?.kind === 'pinch') return

      const anchor = anchorFor(box, corner)
      const axis = axisOf(corner)
      /*
       * Die Schrift wächst nur an den Ecken mit.
       *
       * An einer Kante wäre das falsch: Wer ein Textfeld schmaler zieht, will
       * einen Umbruch und nicht kleinere Buchstaben. Genau diese Trennung macht
       * das Ziehen an einem Textfeld vorhersagbar — Ecke skaliert, Kante setzt.
       */
      const mitSchrift = fontSize !== undefined && scaleContent && axis.x && axis.y
      const frameInhalt = target.type === 'frame' && scaleContent
      const withContent = frameInhalt || mitSchrift
      const content = frameInhalt
        ? contentOfFrame(box, target.id)
        : mitSchrift
          ? [{ target, x: box.x, y: box.y, w: box.w, h: box.h, fontSize }]
          : []
      const moving = new Set([target.id, ...content.map((child) => child.target.id)])
      const targets = collectTargets({ box, elements, frames, moving })
      setActiveFrame(targets.frame)
      // Die gegenüberliegende Ecke bleibt beim Skalieren stehen.
      interaction.current = {
        kind: 'resize',
        target,
        anchorX: anchor.x,
        anchorY: anchor.y,
        /*
         * Mit Inhalt rastet das Seitenverhältnis ein.
         *
         * Nicht aus Bequemlichkeit: Zieht man frei, gibt es zwei Maßstäbe. Jeder,
         * den man dann für den Inhalt wählte, wäre falsch — mit dem einen wandert
         * er senkrecht aus dem Frame, mit einem Mittelwert passt gar nichts mehr.
         * Ein einziger Maßstab ist der einzige Weg, der die Seitenverhältnisse
         * der Elemente unangetastet lässt.
         */
        // Eine Kante kennt kein Seitenverhältnis: Sie soll ja gerade nur ein
        // Maß ändern.
        aspect: withContent ? box.w / box.h : axis.x && axis.y ? aspect : null,
        targets,
        base: box,
        axis,
        content,
        lastContentWrite: 0,
        pendingRect: null,
      }
    },
    [contentOfFrame, elements, frames, scaleContent],
  )

  const interactions = useMemo<LayerInteractions>(
    () => ({
      onSelectFrame: (id, additive) =>
        onSelect(additive ? toggleSelection(selection, 'frame', id) : selectOne('frame', id)),
      onZoomToFrame: (frame) => {
        breakAway.current?.()
        camera.animateToRect(frame, 1.04)
      },
      onElementDragStart: (event, element) => {
        if (!canEdit) return

        // Umschalt-Klick nimmt hinzu oder heraus und verschiebt nicht: Wer die
        // Auswahl gerade zusammenstellt, will sie nicht zugleich bewegen.
        if (event.shiftKey) {
          event.stopPropagation()
          onSelect(toggleSelection(selection, 'element', element.id))
          return
        }

        // Ein Klick auf ein Objekt, das schon in der Auswahl steht, lässt sie
        // bestehen — sonst zerfiele die Mehrfachauswahl beim Anfassen.
        if (!isSelected(selection, 'element', element.id)) {
          onSelect(selectOne('element', element.id))
        }
        bringToFront(doc, element.id)
        beginDrag(event, { type: 'element', id: element.id }, element)
      },
      onElementResizeStart: (event, element, corner) => {
        const axis = axisOf(corner)
        // An der Ecke wächst die Schrift mit, wenn das Werkzeug so eingestellt
        // ist; dann bleibt die Höhe beim Inhalt und wird nicht festgesetzt.
        const mitSchrift = element.type === 'text' && scaleContent && axis.x && axis.y
        /*
         * Wer die Höhe eines Textfelds selbst zieht, will sie behalten. Ohne
         * diese Zeile setzte die Messung sie im nächsten Bild wieder auf die
         * Höhe des Inhalts — das Ziehen bliebe wirkungslos und sähe aus wie ein
         * Fehler.
         */
        if (element.type === 'text' && element.autoHeight && axis.y && !mitSchrift) {
          updateElement(doc, element.id, { autoHeight: false })
        }
        beginResize(
          event,
          { type: 'element', id: element.id },
          element,
          corner,
          // Bilder und Videos behalten ihr Seitenverhältnis, Text und iFrames nicht.
          element.type === 'iframe' || element.type === 'text' ? null : element.w / element.h,
          element.type === 'text' ? element.fontSize : undefined,
        )
      },
      onFrameDragStart: (event, frame) => beginDrag(event, { type: 'frame', id: frame.id }, frame),
      onFrameResizeStart: (event, frame, corner) =>
        beginResize(event, { type: 'frame', id: frame.id }, frame, corner, null),
      onPatchElement: (id, patch) => updateElement(doc, id, patch),
      onPatchFrame: (id, patch) => updateFrame(doc, id, patch),
      onEditIframeUrl,
      onEditText: (id) => richFragment(doc, id),
      onSaveFrameTemplate,
      scaleContent,
      onToggleScaleContent: () =>
        setScaleContent((current) => {
          try {
            localStorage.setItem(SCALE_CONTENT_KEY, current ? '0' : '1')
          } catch {
            // Ohne Speicher gilt die Wahl eben nur für diese Sitzung.
          }
          return !current
        }),
    }),
    [
      beginDrag,
      beginResize,
      camera,
      canEdit,
      doc,
      onEditIframeUrl,
      onSaveFrameTemplate,
      onSelect,
      scaleContent,
      selection,
    ],
  )

  /**
   * Zwei Finger: Abstand bestimmt den Maßstab, ihr Mittelpunkt die Verschiebung.
   *
   * Beides zusammen ist eine einzige Geste — wer aufzieht, verschiebt dabei fast
   * immer auch ein wenig. Getrennt behandelt fühlte sich das Zoomen an, als
   * rutschte der Inhalt unter den Fingern weg.
   */
  const pinchState = useCallback((): { distance: number; x: number; y: number } | null => {
    const [a, b] = [...touches.current.values()]
    if (!a || !b) return null
    return {
      distance: Math.hypot(a.x - b.x, a.y - b.y),
      x: (a.x + b.x) / 2,
      y: (a.y + b.y) / 2,
    }
  }, [])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    // In der Erfassungsphase: Ein zweiter Finger soll die Geste auch dann
    // übernehmen, wenn der erste gerade ein Element zieht.
    const onPointerDown = (event: PointerEvent): void => {
      if (event.pointerType === 'mouse') return
      /*
       * Der erste Finger einer neuen Berührung räumt die Buchführung auf.
       *
       * `isPrimary` heißt genau das: Es liegt sonst nichts mehr auf dem Schirm.
       * Ohne dieses Aufräumen überlebte ein Finger, dessen Loslassen der Browser
       * nicht meldete — beim nächsten Antippen wäre die Zählung bei zwei, und
       * aus einem Verschieben würde grundlos ein Zoomen.
       */
      if (event.isPrimary) touches.current.clear()
      touches.current.set(event.pointerId, { x: event.clientX, y: event.clientY })
      if (touches.current.size !== 2 || viewLocked) return

      const pinch = pinchState()
      if (!pinch || pinch.distance < MIN_PINCH_DISTANCE) return
      breakAway.current?.()
      setGuides({ v: [], h: [] })
      setActiveFrame(null)
      interaction.current = {
        kind: 'pinch',
        lastDistance: pinch.distance,
        lastCenterX: pinch.x,
        lastCenterY: pinch.y,
      }
    }

    container.addEventListener('pointerdown', onPointerDown, { capture: true })
    return () => container.removeEventListener('pointerdown', onPointerDown, { capture: true })
  }, [pinchState, viewLocked])

  useEffect(() => {
    /**
     * Inhalt mitskalieren: eine Streckung des Frames auf seinen neuen Rahmen.
     *
     * Gerechnet wird über die *Ecke des neuen Rahmens*, nicht über den Anker.
     * Beides ist dasselbe, solange man nicht über den Anker hinauszieht — und
     * genau dort läge sonst die Falle: Der Rahmen klappt auf die andere Seite,
     * und ein am Anker gespiegelter Inhalt stünde auf dem Kopf.
     *
     * Alles in einer Transaktion, damit die Streckung bei den anderen
     * Teilnehmern als ein Schritt ankommt statt als Regen aus Einzelwerten.
     */
    const applyContent = (
      current: { base: Rect; content: ScaleChild[] },
      rect: Rect,
    ): void => {
      const s = rect.w / current.base.w
      doc.transact(() => {
        for (const child of current.content) {
          const patch: Record<string, number> = {
            x: rect.x + (child.x - current.base.x) * s,
            y: rect.y + (child.y - current.base.y) * s,
            w: child.w * s,
            h: child.h * s,
          }
          /*
           * Ungerundet gespeichert, gerundet angezeigt.
           *
           * Innerhalb einer Bewegung rechnet jeder Schritt vom Ausgangsmaß aus,
           * es häuft sich also nichts an. Gerundet verschluckte aber jede
           * *abgeschlossene* Bewegung einen halben Punkt — wer ein paarmal
           * vergrößert und wieder verkleinert, fände seine Schriftgrößen nicht
           * wieder.
           */
          if (child.fontSize !== undefined) {
            patch.fontSize = Math.max(MIN_FONT_SIZE, child.fontSize * s)
          }
          if (child.borderWidth !== undefined && child.borderWidth > 0) {
            patch.borderWidth = child.borderWidth * s
          }

          if (child.target.type === 'element') updateElement(doc, child.target.id, patch)
          else updateFrame(doc, child.target.id, patch)
        }
      })
    }

    const onPointerMove = (event: PointerEvent): void => {
      const current = interaction.current

      if (touches.current.has(event.pointerId)) {
        touches.current.set(event.pointerId, { x: event.clientX, y: event.clientY })
      }

      if (current?.kind === 'pinch') {
        const pinch = pinchState()
        if (!pinch || pinch.distance < MIN_PINCH_DISTANCE) return
        const rect = containerRef.current?.getBoundingClientRect()
        if (!rect) return

        // Erst schieben, dann zoomen: Der Zoom hängt am Ankerpunkt, und der ist
        // der Mittelpunkt *nach* der Verschiebung.
        camera.panByScreen(pinch.x - current.lastCenterX, pinch.y - current.lastCenterY)
        camera.zoomAt(
          { x: pinch.x - rect.left, y: pinch.y - rect.top },
          pinch.distance / current.lastDistance,
        )
        current.lastDistance = pinch.distance
        current.lastCenterX = pinch.x
        current.lastCenterY = pinch.y
        return
      }

      const point = boardPointFromEvent(event)
      publishCursor(point.x, point.y)

      if (!current) return

      if (current.kind === 'pan') {
        camera.panByScreen(event.clientX - current.lastX, event.clientY - current.lastY)
        current.lastX = event.clientX
        current.lastY = event.clientY
        return
      }

      if (current.kind === 'marquee' || current.kind === 'frameDraw') {
        const box: Rect = {
          x: Math.min(current.startX, point.x),
          y: Math.min(current.startY, point.y),
          w: Math.abs(point.x - current.startX),
          h: Math.abs(point.y - current.startY),
        }
        current.rect = box
        setMarquee(box)
        return
      }

      // Alt hält das Einrasten an: Wer bewusst dicht neben eine Linie will,
      // käme sonst gegen die Fangweite nicht an.
      const snapping = !event.altKey
      const tolerance = snapping ? SNAP_PX / camera.getCamera().zoom : 0

      if (current.kind === 'drag') {
        let x = point.x - current.grabX
        let y = point.y - current.grabY

        // Einrasten: erst rechnen, dann anwenden — und die getroffenen Linien
        // anzeigen, damit sichtbar ist, woran gerade ausgerichtet wird.
        const box: Rect = { x, y, w: current.w, h: current.h }
        const snapX = findSnap(edgesX(box), current.targets.x, tolerance)
        const snapY = findSnap(edgesY(box), current.targets.y, tolerance)
        if (snapX) x += snapX.delta
        if (snapY) y += snapY.delta
        setGuides({ v: snapX ? [snapX.line] : [], h: snapY ? [snapY.line] : [] })

        applyGeometry(current.target, { x, y })

        // Alles in einer Transaktion, damit ein Frame samt Inhalt bei den
        // anderen Teilnehmern in einem Schritt ankommt statt zu zerfallen.
        if (current.children.length > 0) {
          const dx = x - current.baseX
          const dy = y - current.baseY
          doc.transact(() => {
            for (const child of current.children) {
              applyGeometry(child.target, { x: child.baseX + dx, y: child.baseY + dy })
            }
          })
        }
        return
      }

      const gezogen = snapResize({
        pointer: point,
        anchor: { x: current.anchorX, y: current.anchorY },
        aspect: current.aspect,
        targets: current.targets,
        tolerance,
        minSize: MIN_SIZE,
      })
      // Was der Anfasser nicht anfasst, bleibt wie es war.
      const resized = {
        ...gezogen,
        rect: {
          x: current.axis.x ? gezogen.rect.x : current.base.x,
          y: current.axis.y ? gezogen.rect.y : current.base.y,
          w: current.axis.x ? gezogen.rect.w : current.base.w,
          h: current.axis.y ? gezogen.rect.h : current.base.h,
        },
        guides: {
          v: current.axis.x ? gezogen.guides.v : [],
          h: current.axis.y ? gezogen.guides.h : [],
        },
      }
      setGuides(resized.guides)

      if (current.content.length === 0) {
        applyGeometry(current.target, resized.rect)
        return
      }

      /*
       * Der Frame folgt dem Zeiger in jedem Bild — er ist das, was man beim
       * Ziehen ansieht. Der Inhalt darf gröber nachziehen, sobald es viele
       * Objekte sind: Jedes Bild einzeln zu schreiben kostete bei 150 Elementen
       * über ein halbes Megabyte je Ziehvorgang und sichtbare Aussetzer.
       */
      applyGeometry(current.target, resized.rect)

      const now = performance.now()
      const throttled = current.content.length > THROTTLE_ABOVE
      if (throttled && now - current.lastContentWrite < CONTENT_WRITE_MS) {
        current.pendingRect = resized.rect
        return
      }
      current.lastContentWrite = now
      current.pendingRect = null
      applyContent(current, resized.rect)
    }

    const onPointerUp = (event: PointerEvent): void => {
      touches.current.delete(event.pointerId)

      // Bleibt nach einer Zwei-Finger-Geste ein Finger liegen, wird daraus kein
      // Verschieben: Sonst ruckte das Board in dem Moment, in dem man einen
      // Finger hebt, weil der verbleibende plötzlich als Ziehpunkt gilt.
      if (interaction.current?.kind === 'pinch') {
        interaction.current = null
        return
      }

      if (interaction.current?.kind === 'frameDraw') {
        const box = interaction.current.rect
        interaction.current = null
        setMarquee(null)
        // Ein Klick ohne Fläche ist kein Frame, sondern ein Versehen — dann
        // lieber den Modus verlassen als einen Punkt anzulegen.
        if (box && box.w >= MIN_SIZE && box.h >= MIN_SIZE) onFrameDrawn?.(box)
        else onFrameDrawCancel?.()
        return
      }

      if (interaction.current?.kind === 'marquee') {
        const current = interaction.current
        const box = current.rect
        interaction.current = null
        setMarquee(null)

        // Berührt genügt, vollständig umschlossen wird nicht verlangt: Auf einer
        // unendlichen Fläche zieht man den Rahmen selten sauber um alles herum,
        // und was man streift, hat man gemeint.
        const hits =
          box && (box.w > 2 || box.h > 2)
            ? elements
                .filter(
                  (element) =>
                    element.x < box.x + box.w &&
                    element.x + element.w > box.x &&
                    element.y < box.y + box.h &&
                    element.y + element.h > box.y,
                )
                .map((element) => element.id)
            : []

        if (hits.length === 0) {
          onSelect(current.base)
        } else {
          const previous = selectedIds(current.base, 'element')
          onSelect({ kind: 'element', ids: [...new Set([...previous, ...hits])] })
        }
        return
      }

      // Was die Drosselung übersprungen hat, wird beim Loslassen nachgeholt:
      // Sonst bliebe der Inhalt um bis zu eine Stufe hinter dem Frame zurück.
      if (interaction.current?.kind === 'resize' && interaction.current.pendingRect) {
        applyContent(interaction.current, interaction.current.pendingRect)
      }

      if (interaction.current?.kind === 'pan') {
        setCursorStyle(spaceHeld.current ? 'grab' : 'default')
      }
      if (interaction.current && interaction.current.kind !== 'pan') {
        setGuides({ v: [], h: [] })
        setActiveFrame(null)
      }
      interaction.current = null
    }

    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', onPointerUp)
    window.addEventListener('pointercancel', onPointerUp)
    return () => {
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerup', onPointerUp)
      window.removeEventListener('pointercancel', onPointerUp)
    }
  }, [
    applyGeometry,
    boardPointFromEvent,
    camera,
    doc,
    elements,
    onFrameDrawCancel,
    onFrameDrawn,
    onSelect,
    pinchState,
    publishCursor,
  ])

  /**
   * Escape verlässt „frei ziehen".
   *
   * Ohne das bliebe der Modus stehen, sobald jemand ihn versehentlich wählt:
   * Ein Klick ins Leere legt dann keinen Frame an (zu klein), verlässt den
   * Modus aber auch nicht sichtbar.
   */
  useEffect(() => {
    if (!frameDraw) return
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return
      event.stopPropagation()
      interaction.current = null
      setMarquee(null)
      onFrameDrawCancel?.()
    }
    window.addEventListener('keydown', onKeyDown, true)
    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [frameDraw, onFrameDrawCancel])

  // --- Dateien per Drag & Drop ------------------------------------------------

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault()
      setDropActive(false)
      if (!canEdit) return
      const files = [...event.dataTransfer.files]
      if (files.length === 0) return
      const point = boardPointFromEvent(event)
      onDropFiles(files, point.x, point.y)
    },
    [boardPointFromEvent, canEdit, onDropFiles],
  )

  return (
    <div
      ref={containerRef}
      className={`canvas cursor-${cursorStyle}${dropActive ? ' is-drop-target' : ''}${
        viewLocked ? ' is-locked' : ''
      }${frameDraw ? ' is-frame-draw' : ''}`}
      // Raster- und Rahmenfarbe leiten sich aus der Helligkeit ab: ein weißes
      // Punktraster auf Papierweiß wäre unsichtbar.
      style={
        {
          backgroundColor: background,
          '--board-dot': isLightBackground(background)
            ? 'rgba(0, 0, 0, 0.09)'
            : 'rgba(255, 255, 255, 0.055)',
          '--board-line': isLightBackground(background)
            ? 'rgba(0, 0, 0, 0.28)'
            : 'rgba(255, 255, 255, 0.22)',
        } as React.CSSProperties
      }
      onPointerDown={onBackgroundPointerDown}
      onPointerLeave={() => publishCursor(null, null)}
      onDragOver={(event) => {
        event.preventDefault()
        if (canEdit) setDropActive(true)
      }}
      onDragLeave={() => setDropActive(false)}
      onDrop={onDrop}
    >
      <div className="canvas-grid" aria-hidden="true" />
      <BoardLayer
        ref={layerRef}
        camera={camera}
        elements={elements}
        frames={frames}
        mediaById={mediaById}
        presence={presence}
        selection={selection}
        canEdit={canEdit}
        // Im streng geführten Modus bedient niemand die eingebettete Seite.
        allowIframeInteraction={allowIframeInteraction}
        canWrite={canWrite}
        theme={theme}
        fonts={fonts}
        interactions={interactions}
      >
        {/* Das Raster des Frames, in dem gerade gearbeitet wird — nur während
            der Bewegung, und nur blass: Es ist Orientierung, nicht Inhalt. */}
        {activeFrame && <FrameGrid frame={activeFrame} />}

        {/* Der Auswahlrahmen liegt wie die Hilfslinien im transformierten
            Layer und ist damit in Board-Koordinaten angegeben. */}
        {marquee && (
          <div
            className={`marquee${frameDraw ? ' is-frame' : ''}`}
            aria-hidden="true"
            style={{
              left: `${marquee.x}px`,
              top: `${marquee.y}px`,
              width: `${marquee.w}px`,
              height: `${marquee.h}px`,
            }}
          />
        )}

        {/* Hilfslinien liegen im transformierten Layer: sie sind in
            Board-Koordinaten angegeben und fahren so ohne Rechnung mit. */}
        {(guides.v.length > 0 || guides.h.length > 0) && (
          <div className="guides" aria-hidden="true">
            {guides.v.map((pos) => (
              <div key={`v${pos}`} className="guide guide-v" style={{ left: `${pos}px` }} />
            ))}
            {guides.h.map((pos) => (
              <div key={`h${pos}`} className="guide guide-h" style={{ top: `${pos}px` }} />
            ))}
          </div>
        )}
      </BoardLayer>
      {dropActive && <div className="drop-hint">{t('canvas.dropHint')}</div>}
    </div>
  )
}
